// src/managers/ConvoManager/ConvoState.jsx
import { botManager } from '@/managers/BotManager';

export class ConversationState {
  constructor() {
    this.conversationsMeta = {}; 
    this.currentConversationId = null;
    this.messages = {};
    
    this.isNavigating = false;
    this.defaultAIMemorySize = 20; 
    this.inputEstimation = { content: '', fileCount: 0, textTokens: 0, fileTokens: 0, estimatedTokens: 0 };
    this.isLoadingOlderConversations = false; 
    this.error = null; 
    this.apiKeys = null;
    this.isConvoListListenerAttached = false; 
    this.isAppReady = false; 
    this.rewoundMessagePayload = null;
    
    this.subscribers = new Set();
    
    // PERFORMANCE FIX: Batch notification system
    this._notifyScheduled = false;
    this._batchDepth = 0;
  }

  subscribe(callback) {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  // PERFORMANCE FIX: Coalesce multiple rapid state changes into a single notification
  notify() {
    // If we're inside a batch, don't notify yet
    if (this._batchDepth > 0) {
      this._notifyScheduled = true;
      return;
    }
    
    // Prevent multiple synchronous notifications
    if (this._notifyScheduled) return;
    
    this._notifyScheduled = true;
    
    // Use microtask to batch synchronous updates
    queueMicrotask(() => {
      this._notifyScheduled = false;
      const publicState = this.getPublicState();
      this.subscribers.forEach((callback) => {
        try {
          callback(publicState);
        } catch (error) {
          console.error('[ConvoState] Error in subscriber:', error);
        }
      });
    });
  }

  // PERFORMANCE FIX: Allow explicit batching of multiple operations
  batch(fn) {
    this._batchDepth++;
    try {
      fn();
    } finally {
      this._batchDepth--;
      if (this._batchDepth === 0 && this._notifyScheduled) {
        this._notifyScheduled = false;
        this.notify();
      }
    }
  }

  /**
   * FIX: Always create new message objects to ensure React detects changes.
   * This is critical for proper re-rendering when attachments are removed.
   */
  _upsertMessages(conversationId, messagesToUpdate, sort = true) {
    if (!this.messages[conversationId]) {
      this.messages[conversationId] = [];
    }
  
    const messageMap = new Map(this.messages[conversationId].map(m => [m.id, m]));
    
    messagesToUpdate.forEach(msg => {
      const existingMessage = messageMap.get(msg.id) || {};
      
      // FIX: Create a completely new object instead of spreading into existing
      // This ensures React's shallow comparison detects the change
      const updatedMessage = {
        ...existingMessage,
        ...msg,
        // Ensure arrays are new references
        files: msg.files !== undefined ? [...(msg.files || [])] : [...(existingMessage.files || [])],
        images: msg.images !== undefined ? [...(msg.images || [])] : [...(existingMessage.images || [])],
        contentBlocks: msg.contentBlocks !== undefined ? [...(msg.contentBlocks || [])] : [...(existingMessage.contentBlocks || [])],
      };

      if (updatedMessage.timestamp) {
        if (updatedMessage.timestamp.toDate) {
          updatedMessage.timestamp = updatedMessage.timestamp.toDate();
        } else if (!(updatedMessage.timestamp instanceof Date)) {
          updatedMessage.timestamp = new Date(updatedMessage.timestamp);
        }
        if (isNaN(updatedMessage.timestamp.getTime())) {
          console.warn(`[ConvoState] Invalid timestamp for message ${msg.id}. Using current time.`);
          updatedMessage.timestamp = new Date();
        }
      } else {
        updatedMessage.timestamp = new Date();
      }

      delete updatedMessage.content;
      delete updatedMessage.appendContent;
      delete updatedMessage.retrievedMemoriesContext;
      if ('contentForUI' in msg || 'contentForUI' in existingMessage) {
        updatedMessage.contentForUI = undefined;
      }

      messageMap.set(msg.id, updatedMessage);
    });
  
    const newMessages = Array.from(messageMap.values());
    
    if (sort) {
      newMessages.sort((a, b) => {
        const timeA = a.timestamp?.getTime() || 0;
        const timeB = b.timestamp?.getTime() || 0;
        if (timeA === timeB) return a.id.localeCompare(b.id);
        return timeA - timeB;
      });
    }
    
    this.messages[conversationId] = newMessages;
    // Note: notify() is called by the public methods that call _upsertMessages
  }

  getPublicState() {
    const activeConversationMeta = this.currentConversationId ? this.conversationsMeta[this.currentConversationId] : null;
    const activeMessages = this.currentConversationId ? (this.messages[this.currentConversationId] || []) : [];

    let activeConversation = null;
    if (activeConversationMeta) {
      activeConversation = {
        ...activeConversationMeta,
        // FIX: Create a new array reference for messages to ensure React detects changes
        messages: [...activeMessages], 
      };
    }

    return {
      conversations: this.conversationsMeta,
      messages: this.messages,
      currentConversationId: this.currentConversationId,
      currentConversation: activeConversation,
      optimisticMessages: activeMessages.filter(m => m.isOptimistic),
      isNavigating: this.isNavigating,
      defaultAIMemorySize: this.defaultAIMemorySize,
      inputEstimation: { ...this.inputEstimation },
      isLoadingOlderConversations: this.isLoadingOlderConversations, 
      error: this.error,
      apiKeys: this.apiKeys,
      isConvoListListenerAttached: this.isConvoListListenerAttached,
      isAppReady: this.isAppReady,
      rewoundMessagePayload: this.rewoundMessagePayload,
    };
  }

  setConversationMeta(id, metaUpdates) {
    const existingMeta = this.conversationsMeta[id] || {};
    this.conversationsMeta[id] = {
      id, messageCount: 0, lastMessagePreview: null, messagesLoaded: false, 
      isLoadingMessages: false, isLoadingOlderMessages: false, hasMoreMessages: true, 
      oldestLoadedMessageTimestamp: null, errorLoadingMessages: null, persisted: false, 
      clientSideCreated: null, ...existingMeta, ...metaUpdates,   
    };
    this.notify();
  }

  updateConversationsMeta(updater) {
    if (typeof updater === 'function') {
      this.conversationsMeta = updater(this.conversationsMeta);
    } else {
      const updatedMetas = { ...this.conversationsMeta };
      for (const convoId in updater) {
        if (updater[convoId] === null) { 
          delete updatedMetas[convoId];
        } else {
          updatedMetas[convoId] = { ...(updatedMetas[convoId] || {}), ...updater[convoId], id: convoId };
        }
      }
      this.conversationsMeta = updatedMetas;
    }
    this.notify();
  }

  setCurrentConversationId(id) {
    if (this.currentConversationId !== id) {
      this.currentConversationId = id;
      this.notify();
    }
  }

  /**
   * FIX: Create new message objects to ensure React detects the change
   */
  setMessages(conversationId, messages) {
    // Create new objects for each message to ensure reference changes
    const sortedMessages = messages.map(msg => ({
      ...msg,
      files: [...(msg.files || [])],
      images: [...(msg.images || [])],
      contentBlocks: [...(msg.contentBlocks || [])],
    })).sort((a, b) => {
      const timeA = a.timestamp?.getTime() || 0;
      const timeB = b.timestamp?.getTime() || 0;
      if (timeA === timeB) return a.id.localeCompare(b.id);
      return timeA - timeB;
    });
    this.messages[conversationId] = sortedMessages;
    this.notify();
  }

  prependMessages(conversationId, olderMessages) {
    this._upsertMessages(conversationId, olderMessages, true);
    this.notify();
  }

  addOptimisticMessage(conversationId, message) {
    const messageWithDate = {
      ...message,
      timestamp: message.timestamp instanceof Date ? message.timestamp : new Date(message.timestamp || Date.now()),
      isOptimistic: true,
      files: [...(message.files || [])],
      images: [...(message.images || [])],
      contentBlocks: [...(message.contentBlocks || [])],
    };
    this._upsertMessages(conversationId, [messageWithDate], true);
    this.notify();
  }

  updateOptimisticMessage(conversationId, messageId, updates) {
    this._upsertMessages(conversationId, [{ id: messageId, ...updates }], true);
    this.notify();
  }

  updateMultipleMessages(conversationId, updatesArray) {
    this._upsertMessages(conversationId, updatesArray, true);
    this.notify();
  }

  removeOptimisticMessage(conversationId, messageId) {
    if (this.messages[conversationId]) {
      this.messages[conversationId] = this.messages[conversationId].filter(m => m.id !== messageId);
      this.notify();
    }
  }

  setRewoundMessagePayload(payload) { this.rewoundMessagePayload = payload; this.notify(); }
  clearRewoundMessagePayload() { if (this.rewoundMessagePayload !== null) { this.rewoundMessagePayload = null; this.notify(); } }
  setIsNavigating(navigating) { this.isNavigating = navigating; this.notify(); }
  setError(error) { this.error = error; this.notify(); }
  setIsAppReady(ready) { this.isAppReady = ready; this.notify(); }
  setApiKeys(keys) { this.apiKeys = keys; this.notify(); }
  setIsConvoListListenerAttached(attached) { this.isConvoListListenerAttached = attached; this.notify(); }
  setIsLoadingOlderConversations(loading) { this.isLoadingOlderConversations = loading; this.notify(); }
  setDefaultAIMemorySize(size) { this.defaultAIMemorySize = size; this.notify(); }
  setInputEstimation(estimation) { this.inputEstimation = { ...estimation }; this.notify(); }

  isConversationLoading(convoId) {
    const meta = this.conversationsMeta[convoId];
    if (!meta) return true;
    return meta.isLoadingMessages || (!meta.messagesLoaded && meta.persisted);
  }

  getManifestForConversation(id) {
    return this.conversationsMeta[id]?.parManifest || null;
  }

  clearPendingMessage(conversationId) {
    if (this.conversationsMeta[conversationId]?.pendingMessage) {
      this.setConversationMeta(conversationId, { pendingMessage: null });
    }
  }
}